import {
  Button,
  Card,
  Input,
  Popconfirm,
  TreeSelect,
  Pagination,
  Tag,
  Tooltip,
  Select,
  Checkbox,
  Modal,
} from 'antd'
import {
  SearchOutlined,
  FileTextOutlined,
  DeleteOutlined,
  FilterOutlined,
  CopyOutlined,
} from '@ant-design/icons'

const { Option } = Select
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import FadeIn from 'src/components/styled/FadeIn'
import { resetCatalogueSearch } from 'src/features/CatalogueSearchSlice'
import { resetOrdersFilter } from 'src/features/FilterSlice'
import { resetOrders } from 'src/features/OrdersSlice'
import { fetchAllSuppliers } from 'src/features/SupplierSlice'
import { resetVenueProducts } from 'src/features/VenueProductsSlice'
import {
  createVenue,
  deleteVenue,
  pushAppNotification,
  setVenue,
} from 'src/features/VenueSlice'
import { resetVenueSuppliers } from 'src/features/VenueSuppliersSlice'
import styled from 'styled-components/macro'
import VenuesReportModal from './VenuesReportModal'
import { getVenues, searchVenues } from 'src/features/AuthSlice'
import { SuccessNotification } from 'src/notifications/SuccessNotification'
import { RejectedNotification } from 'src/notifications/SuccessNotification'
import { API } from 'src/utils/interceptor'
import { hasBrandAdminAccess, canManageAnyVenue } from 'src/utils/adminAccess'
import { planDisplayName } from 'src/constants/planDisplay'
import {
  groupsForAddPicker,
  addToGroupsOutcome,
  groupsByVenue,
  venuesForGroupFilter,
} from 'src/screens/venues/addToGroupSearch'
import VenueGroupMembershipChips from 'src/screens/venues/VenueGroupMembershipChips'
import { filterVenuesForGroupFill } from 'src/screens/venues/venueListFilters'
import { fetchEveryVenuePage } from 'src/screens/venues/venueListPages'

const Venues = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const username = useSelector(state => state.auth.cognito.username)
  const account = useSelector(state => state.auth?.account)
  const isBrandAdmin = hasBrandAdminAccess(account)
  const venues = useSelector(state => state.auth.account.venues)
  const isAdmin = useSelector(state => state.auth.account.isAdmin)
  const canAddToGroup = canManageAnyVenue(account)

  const [venueTitle, setVenueTitle] = useState('')
  const [pushNotificationText, setPushNotificationText] = useState('')
  const [selectedVenueIds, setSelectedVenuesIds] = useState([])
  const [selectedVenuesForDelete, setSelectedVenuesForDelete] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [activityFilter, setActivityFilter] = useState('all')
  const [visibleVenuesModal, setVsibleVenuesModal] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [isDeleting, setIsDeleting] = useState(false)
  const [addToGroupOpen, setAddToGroupOpen] = useState(false)
  const [groupQuery, setGroupQuery] = useState('')
  const [groupPaste, setGroupPaste] = useState('')
  const [groups, setGroups] = useState([])
  const [chosenGroups, setChosenGroups] = useState([])
  const [addingToGroup, setAddingToGroup] = useState(false)
  const [groupFilter, setGroupFilter] = useState('all')
  const [planFilter, setPlanFilter] = useState('all')
  const [brandFilter, setBrandFilter] = useState('all')
  const [orderedBefore, setOrderedBefore] = useState('')
  const [venueBrands, setVenueBrands] = useState([])
  const [listPage, setListPage] = useState(1)
  const [restVenues, setRestVenues] = useState([])
  const [loadingRest, setLoadingRest] = useState(false)
  const restKey = useRef('')
  const itemsPerPage = 200
  const listLimit = canAddToGroup ? 2000 : itemsPerPage
  const totalPages = useSelector(state => state.auth?.account?.totalPages)
  const paginationVenuesLoading = useSelector(
    state => state.auth?.account?.paginationVenuesLoading,
  )

  useEffect(() => {
    dispatch(setVenue(null))
    dispatch(fetchAllSuppliers())
    dispatch(resetOrdersFilter())
    dispatch(resetVenueSuppliers())
    dispatch(resetVenueProducts())
    dispatch(resetCatalogueSearch())
    dispatch(resetOrders())
  }, [])
  useEffect(() => {
    if (!canAddToGroup) return undefined
    API.get('api2', 'venue-groups')
      .then(response => setGroups(response?.data || []))
      .catch(() => setGroups([]))
    API.get('api2', 'venue-groups/venue-brands')
      .then(response => setVenueBrands(response?.data || []))
      .catch(() => setVenueBrands([]))
    return undefined
  }, [canAddToGroup])

  const listedVenues = useMemo(() => {
    if (!canAddToGroup) return venues
    const seen = new Set()
    const rows = []
    for (const venue of [...(venues || []), ...restVenues]) {
      const id = String(venue?._id || '')
      if (!id || seen.has(id)) continue
      seen.add(id)
      rows.push(venue)
    }
    return rows
  }, [canAddToGroup, venues, restVenues])

  useEffect(() => {
    restKey.current = ''
    setRestVenues([])
  }, [activityFilter, searchQuery])

  useEffect(() => {
    if (!canAddToGroup || paginationVenuesLoading || !username) return undefined
    if (!totalPages || totalPages <= 1) {
      setLoadingRest(false)
      return undefined
    }
    const title = hasSearched ? String(searchQuery || '').trim() : ''
    const activity = activityFilter !== 'all' ? activityFilter : ''
    const key = `${username}|${title}|${activity}|${totalPages}|${
      (venues || []).length
    }`
    if (restKey.current === key) return undefined
    let cancelled = false
    setLoadingRest(true)
    const restPages = fetchEveryVenuePage({
      username,
      title,
      activity,
      pageSize: listLimit,
      get: async (api, path) => {
        if (path.includes('page=1&'))
          return { venues, pagination: { totalPages } }
        return API.get(api, path)
      },
    })
      .then(rows => {
        if (cancelled) return null
        restKey.current = key
        const firstIds = new Set((venues || []).map(venue => String(venue._id)))
        setRestVenues(rows.filter(venue => !firstIds.has(String(venue._id))))
        return null
      })
      .catch(() => {
        if (!cancelled) setRestVenues([])
        return null
      })
      .finally(() => {
        if (!cancelled) setLoadingRest(false)
      })
    restPages.catch(() => null)
    return () => {
      cancelled = true
    }
  }, [
    canAddToGroup,
    paginationVenuesLoading,
    username,
    totalPages,
    activityFilter,
    hasSearched,
    searchQuery,
    venues,
    listLimit,
  ])

  const brandById = useMemo(
    () =>
      new Map(
        (venueBrands || []).map(venue => [
          String(venue._id),
          venue.brand || '',
        ]),
      ),
    [venueBrands],
  )
  const matchedVenues = useMemo(
    () =>
      filterVenuesForGroupFill(
        venuesForGroupFilter(listedVenues, groups, groupFilter),
        {
          plan: planFilter,
          brand: brandFilter,
          activity: activityFilter,
          orderedBefore,
          brands: brandById,
        },
      ),
    [
      listedVenues,
      groups,
      groupFilter,
      planFilter,
      brandFilter,
      activityFilter,
      orderedBefore,
      brandById,
    ],
  )
  const visibleVenues = matchedVenues.slice(
    (listPage - 1) * itemsPerPage,
    listPage * itemsPerPage,
  )
  const localPages = Math.max(1, Math.ceil(matchedVenues.length / itemsPerPage))
  useEffect(() => {
    const query = typeof searchQuery === 'string' ? searchQuery.trim() : ''
    if (!query || !username) return undefined
    const handle = window.setTimeout(() => {
      setHasSearched(true)
      if (currentPage === 1) {
        dispatch(
          searchVenues({
            title: query,
            username,
            page: 1,
            limit: listLimit,
            activity: activityFilter !== 'all' ? activityFilter : undefined,
          }),
        )
      } else {
        setCurrentPage(1)
      }
    }, 250)
    return () => window.clearTimeout(handle)
  }, [searchQuery])

  useEffect(() => {
    if (searchQuery === '' && hasSearched) {
      if (currentPage === 1) {
        setHasSearched(false)
        dispatch(
          getVenues({
            username,
            page: 1,
            limit: listLimit,
            activity: activityFilter !== 'all' ? activityFilter : undefined,
          }),
        )
      } else {
        setHasSearched(false)
        setCurrentPage(1)
      }
    }
  }, [searchQuery])

  useEffect(() => {
    // Clear selections when page or filter changes
    setSelectedVenuesForDelete([])

    if (!hasSearched && !(currentPage > totalPages)) {
      dispatch(
        getVenues({
          page: currentPage,
          limit: listLimit,
          username,
          activity: activityFilter !== 'all' ? activityFilter : undefined,
        }),
      )
    } else if (hasSearched && !(currentPage > totalPages)) {
      dispatch(
        searchVenues({
          title: searchQuery,
          username,
          page: currentPage,
          limit: listLimit,
          activity: activityFilter !== 'all' ? activityFilter : undefined,
        }),
      )
    }
  }, [currentPage, activityFilter])

  const membership = useMemo(() => groupsByVenue(groups), [groups])

  const handleCardClick = venue => {
    dispatch(setVenue(venue))
    history.push(`/v/${venue._id}`)
  }

  const handleConfirm = (event, venueId) => {
    event.stopPropagation()
    dispatch(deleteVenue(venueId))
  }

  const onClickCreateVenue = () => {
    setVenueTitle('')
    dispatch(
      createVenue({
        title: venueTitle,
        username: username,
        TFRVenue: account?.TFRAdmin,
        JUSTVenue: account?.JUSTAdmin,
        GREENVenue: account?.GREENAdmin,
      }),
    )
  }

  const onClickPushNotification = () => {
    setPushNotificationText('')
    dispatch(pushAppNotification(pushNotificationText, selectedVenueIds))
  }
  const venueOptions = listedVenues.map(item => {
    return {
      title: item.title,
      value: item._id,
    }
  })
  const allIds = venueOptions.map(({ value }) => value)

  // Helper function to get Tag color from activity color
  const getTagColor = activityColor => {
    switch (activityColor) {
      case 'green':
        return 'green'
      case 'amber':
        return 'orange'
      case 'red':
        return 'red'
      case 'purple':
        return 'magenta'
      default:
        return 'default'
    }
  }

  // Helper function to get tooltip text
  const getTooltipText = activityColor => {
    switch (activityColor) {
      case 'green':
        return 'Active – last order within 30 days'
      case 'amber':
        return 'Dormant – last order between 31 and 365 days'
      case 'red':
        return 'Inactive – last order over a year ago'
      case 'purple':
        return 'Never ordered'
      default:
        return ''
    }
  }

  // Helper function to get border color for selected cards
  const getBorderColor = activityColor => {
    switch (activityColor) {
      case 'green':
        return '#52c41a'
      case 'amber':
        return '#fa8c16'
      case 'red':
        return '#ff4d4f'
      case 'purple':
        return '#eb2f96'
      default:
        return '#1890ff'
    }
  }

  // Helper function to get subtle background tint for activity
  const getActivityBackgroundTint = activityColor => {
    switch (activityColor) {
      case 'green':
        return 'rgba(82, 196, 26, 0.05)'
      case 'amber':
        return 'rgba(250, 140, 22, 0.05)'
      case 'red':
        return 'rgba(255, 77, 79, 0.05)'
      case 'purple':
        return 'rgba(235, 47, 150, 0.05)'
      default:
        return 'transparent'
    }
  }

  // Internal plan key (never shown to customers; use for logic/comparisons)
  const getSubscriptionPlanKey = newPricing => {
    const pricing = newPricing || {}

    if (pricing.pro?.isSubscribed) return 'Pro'
    if (pricing.growth?.isSubscribed) return 'Growth'
    if (pricing.starter?.isSubscribed) return 'Starter'

    // Default for older venues / venues without a paid plan
    return 'Free'
  }

  // Customer-facing subscription plan label based on new pricing
  const getSubscriptionPlan = newPricing => {
    const key = getSubscriptionPlanKey(newPricing)
    return key === 'Free' ? planDisplayName(key) : `${key} Plan`
  }

  // Helper function to get tag color for subscription plan
  const getSubscriptionColor = newPricing => {
    const plan = getSubscriptionPlanKey(newPricing)

    switch (plan) {
      case 'Pro':
        return 'green'
      case 'Growth':
        return 'gold'
      case 'Starter':
        return 'blue'
      default:
        return 'default'
    }
  }

  // Toggle venue selection for delete
  const toggleVenueSelection = (venueId, event) => {
    event.stopPropagation()
    setSelectedVenuesForDelete(prev =>
      prev.includes(venueId)
        ? prev.filter(id => id !== venueId)
        : [...prev, venueId],
    )
  }

  // Toggle select all (current page only)
  const toggleSelectAll = () => {
    if (
      matchedVenues.length > 0 &&
      matchedVenues.every(venue => selectedVenuesForDelete.includes(venue._id))
    ) {
      setSelectedVenuesForDelete([])
    } else {
      setSelectedVenuesForDelete(matchedVenues.map(venue => venue._id))
    }
  }

  const openAddToGroup = () => {
    setGroupQuery('')
    setGroupPaste('')
    setChosenGroups([])
    setAddToGroupOpen(true)
    API.get('api2', 'venue-groups')
      .then(response => setGroups(response?.data || []))
      .catch(() => setGroups([]))
  }

  const addSelectedToGroups = async () => {
    if (!chosenGroups.length || !selectedVenuesForDelete.length) return
    setAddingToGroup(true)
    const added = []
    const failed = []
    for (const groupId of chosenGroups) {
      const name =
        groups.find(group => group._id === groupId)?.name || 'A group'
      try {
        await API.post('api2', `venue-groups/${groupId}/venues`, {
          body: { venueIds: selectedVenuesForDelete },
        })
        added.push({ id: groupId, name })
      } catch (error) {
        failed.push({
          id: groupId,
          name,
          reason:
            error?.response?.data?.message || 'Could not add those venues.',
        })
      }
    }
    const outcome = addToGroupsOutcome({
      venueCount: selectedVenuesForDelete.length,
      added,
      failed,
    })
    if (outcome.ok) {
      SuccessNotification({ message: outcome.message })
      setAddToGroupOpen(false)
      setSelectedVenuesForDelete([])
      API.get('api2', 'venue-groups')
        .then(response => setGroups(response?.data || []))
        .catch(() => setGroups([]))
    } else {
      RejectedNotification({ message: outcome.message })
      setChosenGroups(failed.map(group => group.id))
    }
    setAddingToGroup(false)
  }

  const groupPicker = groupsForAddPicker(groups, groupQuery, groupPaste)

  const selectPastedGroups = () => {
    const ids = groupPicker.pastedMatches.map(group => group._id)
    setChosenGroups(current => [...new Set([...current, ...ids])])
  }

  const isAllSelected =
    matchedVenues.length > 0 &&
    matchedVenues.every(venue => selectedVenuesForDelete.includes(venue._id))

  // Copy venue code to clipboard
  const copyVenueCode = (code, event) => {
    event.stopPropagation()
    navigator.clipboard
      .writeText(code)
      .then(() => {
        SuccessNotification({
          message: 'Venue code copied to clipboard',
        })
        return true
      })
      .catch(error => {
        console.error('Failed to copy venue code:', error)
        RejectedNotification({
          message: 'Failed to copy venue code',
        })
      })
  }

  // Handle bulk delete
  const handleBulkDelete = () => {
    const count = selectedVenuesForDelete.length
    Modal.confirm({
      title: 'Delete Selected Venues',
      content: `You are about to permanently delete ${count} venue${
        count > 1 ? 's' : ''
      }. This action cannot be undone. Please confirm you wish to continue.`,
      okText: 'Confirm Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsDeleting(true)
        let successCount = 0
        let failCount = 0

        // Delete venues sequentially (calling API directly to avoid page reload)
        for (const venueId of selectedVenuesForDelete) {
          try {
            await API.del('api', `venue/${venueId}`)
            successCount++
          } catch (error) {
            failCount++
            console.error(`Failed to delete venue ${venueId}:`, error)
          }
        }

        setIsDeleting(false)
        setSelectedVenuesForDelete([])

        // Show results
        if (failCount === 0) {
          SuccessNotification({
            message: `${successCount} venue${
              successCount > 1 ? 's' : ''
            } deleted successfully`,
          })
        } else {
          RejectedNotification({
            message: `${successCount} venue${
              successCount > 1 ? 's' : ''
            } deleted successfully, ${failCount} failed`,
            description:
              'One or more venues could not be deleted. Please refresh and try again.',
          })
        }

        // Refresh the venue list
        if (hasSearched && searchQuery && searchQuery.trim()) {
          dispatch(
            searchVenues({
              title: searchQuery,
              username,
              page: currentPage,
              limit: listLimit,
              activity: activityFilter !== 'all' ? activityFilter : undefined,
            }),
          )
        } else {
          dispatch(
            getVenues({
              page: currentPage,
              limit: listLimit,
              username,
              activity: activityFilter !== 'all' ? activityFilter : undefined,
            }),
          )
        }
      },
    })
  }

  return (
    <Wrapper>
      <VenuesReportModal
        visibleVenuesModal={visibleVenuesModal}
        setVsibleVenuesModal={setVsibleVenuesModal}
      />
      <Modal
        open={addToGroupOpen}
        title='Add to group'
        okText='Add'
        confirmLoading={addingToGroup}
        okButtonProps={{ disabled: chosenGroups.length === 0 }}
        onOk={addSelectedToGroups}
        onCancel={() => setAddToGroupOpen(false)}
      >
        <p>
          This only adds the selected venues to the group. Nothing is shared.
        </p>
        <Input
          value={groupQuery}
          onChange={event => setGroupQuery(event.target.value)}
          placeholder='Name contains'
          style={{ marginBottom: 8 }}
        />
        <Input.TextArea
          rows={3}
          value={groupPaste}
          onChange={event => setGroupPaste(event.target.value)}
          placeholder='Paste a list of group names'
        />
        <Button
          style={{ marginTop: 8 }}
          disabled={!groupPaste.trim()}
          onClick={selectPastedGroups}
        >
          Select matching groups
        </Button>
        {groupPicker.unmatched.length > 0 && (
          <p>Not found: {groupPicker.unmatched.join(', ')}</p>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            marginTop: 12,
          }}
        >
          {groupPicker.matches.map(group => (
            <label key={group._id}>
              <Checkbox
                checked={chosenGroups.includes(group._id)}
                onChange={() =>
                  setChosenGroups(current =>
                    current.includes(group._id)
                      ? current.filter(id => id !== group._id)
                      : [...current, group._id],
                  )
                }
              />
              {group.name}
            </label>
          ))}
        </div>
      </Modal>
      <FilterWrapper>
        <FilterContent>
          <FilterTopRow>
            {canAddToGroup && (
              <SelectAllCheckboxWrapper>
                <Checkbox
                  checked={isAllSelected}
                  onChange={toggleSelectAll}
                  style={{
                    marginRight: 12,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <SelectAllText>
                    {matchedVenues.length > visibleVenues.length
                      ? `Select all (${matchedVenues.length})`
                      : 'Select All'}
                  </SelectAllText>
                </Checkbox>
              </SelectAllCheckboxWrapper>
            )}
            <Input
              onChange={e => {
                const next = e?.target?.value
                setSearchQuery(typeof next === 'string' ? next : '')
              }}
              value={typeof searchQuery === 'string' ? searchQuery : ''}
              placeholder={'Enter venue title'}
              style={{ flex: '1 1 auto', minWidth: 200 }}
            />
          </FilterTopRow>
          {loadingRest && <div>Loading the rest of your venues…</div>}
          <FilterBottomRow>
            <Button
              icon={<SearchOutlined />}
              style={{
                background: '#b5d922',
                width: 250,
                color: 'white',
                flexShrink: 0,
              }}
              onClick={() => {
                if (searchQuery && searchQuery.trim()) {
                  if (currentPage === 1) {
                    setHasSearched(true)
                    dispatch(
                      searchVenues({
                        title: searchQuery,
                        username,
                        page: 1,
                        limit: listLimit,
                        activity:
                          activityFilter !== 'all' ? activityFilter : undefined,
                      }),
                    )
                  } else {
                    setHasSearched(true)
                    setCurrentPage(1)
                  }
                }
              }}
            >
              Search
            </Button>
            <Select
              value={activityFilter}
              onChange={value => {
                setActivityFilter(value)
                setCurrentPage(1)
                if (hasSearched && searchQuery && searchQuery.trim()) {
                  dispatch(
                    searchVenues({
                      title: searchQuery,
                      username,
                      page: 1,
                      limit: listLimit,
                      activity: value !== 'all' ? value : undefined,
                    }),
                  )
                } else {
                  dispatch(
                    getVenues({
                      page: 1,
                      limit: listLimit,
                      username,
                      activity: value !== 'all' ? value : undefined,
                    }),
                  )
                }
              }}
              style={{
                width: 200,
                flexShrink: 0,
              }}
              placeholder='Filter by Activity'
              suffixIcon={<FilterOutlined />}
            >
              <Option value='all' style={{ color: '#1890ff' }}>
                All Venues
              </Option>
              <Option value='green' style={{ color: '#52c41a' }}>
                Active
              </Option>
              <Option value='amber' style={{ color: '#fa8c16' }}>
                Dormant
              </Option>
              <Option value='red' style={{ color: '#ff4d4f' }}>
                Inactive
              </Option>
              <Option value='purple' style={{ color: '#eb2f96' }}>
                Never Ordered
              </Option>
            </Select>
            {canAddToGroup && (
              <Select
                value={groupFilter}
                onChange={value => {
                  setGroupFilter(value)
                  setListPage(1)
                  setSelectedVenuesForDelete([])
                  if (currentPage !== 1) setCurrentPage(1)
                }}
                style={{ width: 220, flexShrink: 0 }}
                placeholder='Filter by group'
              >
                <Option value='all'>All groups</Option>
                <Option value='none'>No group</Option>
                {groups.map(group => (
                  <Option key={group._id} value={group._id}>
                    {group.name}
                  </Option>
                ))}
              </Select>
            )}
            {canAddToGroup && (
              <Select
                value={planFilter}
                onChange={value => {
                  setPlanFilter(value)
                  setListPage(1)
                  setSelectedVenuesForDelete([])
                }}
                style={{ width: 160, flexShrink: 0 }}
              >
                <Option value='all'>All plans</Option>
                <Option value='Free Plan'>Free</Option>
                <Option value='Starter Plan'>Starter</Option>
                <Option value='Growth Plan'>Growth</Option>
                <Option value='Pro Plan'>Pro</Option>
              </Select>
            )}
            {canAddToGroup && (
              <Select
                value={brandFilter}
                onChange={value => {
                  setBrandFilter(value)
                  setListPage(1)
                  setSelectedVenuesForDelete([])
                }}
                style={{ width: 150, flexShrink: 0 }}
              >
                <Option value='all'>All brands</Option>
                <Option value='TFR'>TFR</Option>
                <Option value='GREEN'>GREEN</Option>
                <Option value='JUST'>JUST</Option>
              </Select>
            )}
            {canAddToGroup && (
              <input
                type='date'
                aria-label='Last ordered before'
                value={orderedBefore}
                onChange={event => {
                  setOrderedBefore(event.target.value)
                  setListPage(1)
                  setSelectedVenuesForDelete([])
                }}
                style={{ height: 32, flexShrink: 0 }}
              />
            )}
            <Button
              icon={<FileTextOutlined />}
              style={{
                background: '#b5d922',
                width: 250,
                color: 'white',
                flexShrink: 0,
              }}
              type={'primary'}
              onClick={() => setVsibleVenuesModal(true)}
            >
              Reports
            </Button>
            {canAddToGroup && selectedVenuesForDelete.length > 0 && (
              <Button onClick={openAddToGroup}>
                Add to group ({selectedVenuesForDelete.length})
              </Button>
            )}
            {isAdmin && selectedVenuesForDelete.length > 0 && (
              <Button
                danger
                icon={<DeleteOutlined />}
                style={{
                  width: 200,
                  flexShrink: 0,
                }}
                onClick={handleBulkDelete}
                disabled={isDeleting}
                loading={isDeleting}
              >
                {isDeleting
                  ? 'Deleting...'
                  : `Delete Selected (${selectedVenuesForDelete.length})`}
              </Button>
            )}
          </FilterBottomRow>
        </FilterContent>
        {totalPages > 1 && !canAddToGroup && (
          <PaginationContainer>
            <Pagination
              current={currentPage}
              total={totalPages * itemsPerPage}
              pageSize={itemsPerPage}
              onChange={pageNumber => setCurrentPage(pageNumber)}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} venues`
              }
            />
          </PaginationContainer>
        )}
        {canAddToGroup && localPages > 1 && (
          <PaginationContainer>
            <Pagination
              current={listPage}
              total={matchedVenues.length}
              pageSize={itemsPerPage}
              onChange={pageNumber => setListPage(pageNumber)}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} venues`
              }
            />
          </PaginationContainer>
        )}
      </FilterWrapper>
      <CardWrapper>
        {paginationVenuesLoading ? (
          <SkeletonGrid>
            {[...Array(8)].map((_, index) => (
              <SkeletonCard key={index}>
                <SkeletonPlaceholder
                  style={{ height: 120, marginBottom: 12 }}
                />
                <SkeletonPlaceholder
                  style={{ height: 20, width: '60%', marginBottom: 8 }}
                />
                <SkeletonPlaceholder
                  style={{ height: 16, width: '80%', marginBottom: 8 }}
                />
                <SkeletonPlaceholder
                  style={{ height: 16, width: '70%', marginBottom: 8 }}
                />
                <SkeletonPlaceholder style={{ height: 16, width: '50%' }} />
              </SkeletonCard>
            ))}
          </SkeletonGrid>
        ) : matchedVenues.length === 0 ? (
          <EmptyStateWrapper>
            <EmptyDescription>
              <EmptyIcon>📋</EmptyIcon>
              <EmptyTitle>No venues found</EmptyTitle>
              <EmptyText>
                {hasSearched ||
                activityFilter !== 'all' ||
                groupFilter !== 'all' ||
                planFilter !== 'all' ||
                brandFilter !== 'all' ||
                orderedBefore
                  ? 'No venues match your current search or filter criteria. Try adjusting your filters.'
                  : 'No venues available. Create a new venue to get started.'}
              </EmptyText>
              {(hasSearched ||
                activityFilter !== 'all' ||
                groupFilter !== 'all' ||
                planFilter !== 'all' ||
                brandFilter !== 'all' ||
                orderedBefore) && (
                <Button
                  type='primary'
                  onClick={() => {
                    setSearchQuery('')
                    setActivityFilter('all')
                    setGroupFilter('all')
                    setPlanFilter('all')
                    setBrandFilter('all')
                    setOrderedBefore('')
                    setListPage(1)
                    setHasSearched(false)
                    dispatch(
                      getVenues({
                        page: 1,
                        limit: listLimit,
                        username,
                      }),
                    )
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </EmptyDescription>
          </EmptyStateWrapper>
        ) : (
          visibleVenues.map(venue => {
            const {
              _id: venueId,
              addressLine1,
              addressLine2,
              addressCity,
              addressPostCode,
              phoneNumber,
              title,
              code,
              lastOrderDate,
              createdAt,
              activityColor,
              newPricing,
            } = venue
            const hasAllSettings =
              addressLine1 &&
              addressLine2 &&
              addressCity &&
              addressPostCode &&
              phoneNumber

            const isSelected = selectedVenuesForDelete.includes(venueId)
            const borderColor = isSelected
              ? getBorderColor(activityColor)
              : 'transparent'
            const activityBgTint = getActivityBackgroundTint(activityColor)

            return (
              <CardComp
                key={venueId}
                onClick={() => handleCardClick(venue)}
                isSelected={isSelected}
                borderColor={borderColor}
                activityBgTint={activityBgTint}
              >
                {canAddToGroup && (
                  <CheckboxContainer onClick={e => e.stopPropagation()}>
                    <SelectAllCheckboxWrapper>
                      <Checkbox
                        checked={isSelected}
                        onChange={e => toggleVenueSelection(venueId, e)}
                        onClick={e => e.stopPropagation()}
                      />
                    </SelectAllCheckboxWrapper>
                  </CheckboxContainer>
                )}
                <LogoContainer>
                  {venue?.venueLogo ? (
                    <Img
                      src={venue.venueLogo}
                      alt={`${title || 'Venue'} logo`}
                      onError={event => {
                        console.log('error 0--------------------->', event)
                        // Fallback to initials if the image URL is broken
                        event.target.onerror = null
                        event.target.style.display = 'none'
                        const container = event.target.parentNode
                        if (
                          container &&
                          !container.querySelector('.logo-initials')
                        ) {
                          const span = document.createElement('span')
                          span.className = 'logo-initials'
                          span.textContent = (title || '?')
                            .toString()
                            .trim()
                            .charAt(0)
                            .toUpperCase()
                          container.appendChild(span)
                        }
                      }}
                    />
                  ) : (
                    <LogoInitials className='logo-initials'>
                      {(title || '?').toString().trim().charAt(0).toUpperCase()}
                    </LogoInitials>
                  )}
                </LogoContainer>
                <H3>{title}</H3>
                {canAddToGroup && (
                  <VenueGroupMembershipChips
                    groups={membership.get(String(venueId)) || []}
                  />
                )}
                {activityColor && (
                  <ActivityContainer>
                    <Tooltip title={getTooltipText(activityColor)}>
                      <StyledTag color={getTagColor(activityColor)}>
                        {activityColor === 'green'
                          ? 'Active'
                          : activityColor === 'amber'
                          ? 'Dormant'
                          : activityColor === 'red'
                          ? 'Inactive'
                          : 'Never Ordered'}
                      </StyledTag>
                    </Tooltip>
                    <StyledTag
                      color={getSubscriptionColor(newPricing)}
                      style={
                        getSubscriptionPlanKey(newPricing) === 'Free'
                          ? { color: '#000' }
                          : undefined
                      }
                    >
                      {getSubscriptionPlan(newPricing)}
                    </StyledTag>
                    <ActivityText>
                      Last order: {lastOrderDate || '—'} | Created:{' '}
                      {createdAt || '—'}
                    </ActivityText>
                  </ActivityContainer>
                )}
                <StyledContainer>
                  {hasAllSettings && code ? (
                    <CodeContainer>
                      <CodeText>{code}</CodeText>
                      <CopyIconWrapper
                        onClick={e => copyVenueCode(code, e)}
                        title='Copy venue code'
                      >
                        <CopyOutlined />
                      </CopyIconWrapper>
                    </CodeContainer>
                  ) : (
                    <IncompleteSetup>
                      Complete set up to get a code
                    </IncompleteSetup>
                  )}
                  <CardButton
                    title='Are you sure to delete this venue?'
                    placement='topRight'
                    onConfirm={event => handleConfirm(event, venueId)}
                    onCancel={e => e.stopPropagation()}
                    okText='Yes'
                    cancelText='No'
                  >
                    {isAdmin && (
                      <Button
                        type={'danger'}
                        size={'small'}
                        icon={<DeleteOutlined />}
                        onClick={e => e.stopPropagation()}
                      >
                        Delete
                      </Button>
                    )}
                  </CardButton>
                </StyledContainer>
              </CardComp>
            )
          })
        )}
      </CardWrapper>

      {(venues.length < 1 || isAdmin || isBrandAdmin) && (
        <Row>
          <ActionCard>
            <h4>Create venue</h4>
            <InputComp
              value={venueTitle}
              onChange={e => {
                setVenueTitle(e.target.value)
              }}
              placeholder='Venue title'
            />
            <ActionButton
              title='Are you sure to create this venue?'
              placement='topRight'
              onConfirm={event => onClickCreateVenue(event)}
              okText='Yes'
              cancelText='No'
            >
              <Button style={{ marginTop: '42px' }}>Add</Button>
            </ActionButton>
          </ActionCard>

          {(isAdmin || isBrandAdmin) && (
            <ActionCard>
              <h4>Send push notification to venues</h4>
              <InputComp
                value={pushNotificationText}
                onChange={e => {
                  setPushNotificationText(e.target.value)
                }}
                placeholder='Notification text'
              />
              <TreeSelect
                treeNodeFilterProp='title'
                allowClear={true}
                placeholder='Select a venue'
                treeCheckable={true}
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                dropdownStyle={{ maxHeight: '300px' }}
                style={{ marginBottom: '15px' }}
                onChange={ids => setSelectedVenuesIds(ids)}
                value={selectedVenueIds}
                maxTagCount={1}
                maxTagPlaceholder={omittedValues =>
                  `+ ${omittedValues.length} Venues ...`
                }
                treeData={[
                  {
                    title:
                      selectedVenueIds.length > 0 ? (
                        <span
                          onClick={() => setSelectedVenuesIds([])}
                          style={{
                            display: 'inline-block',
                            color: '#b5d922',
                            cursor: 'pointer',
                          }}
                        >
                          Unselect all
                        </span>
                      ) : (
                        <span
                          onClick={() => setSelectedVenuesIds(allIds)}
                          style={{
                            display: 'inline-block',
                            color: '#b5d922',
                            cursor: 'pointer',
                          }}
                        >
                          Select all
                        </span>
                      ),
                    value: 'xxx',
                    disableCheckbox: true,
                    disabled: true,
                  },
                  ...venueOptions,
                ]}
              />
              <ActionButton
                title='Are you sure to send this notification?'
                placement='topRight'
                onConfirm={onClickPushNotification}
                okText='Yes'
                cancelText='No'
              >
                <Button>Send</Button>
              </ActionButton>
            </ActionCard>
          )}
        </Row>
      )}
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.xxl}`};
  background: linear-gradient(to bottom, #fafafa 0%, #ffffff 100%);
  min-height: 100vh;
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.md}`};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.sm}`};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.xs}`};
  }
`

const CardWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    gap: 16px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    gap: 12px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    gap: 8px;
  }
`

const CardComp = styled(Card)`
  cursor: pointer;
  background: ${({ activityBgTint, theme }) =>
    activityBgTint || theme.colours.mayoWhite};
  margin: ${({ theme }) => theme.spacing.sm} 0 !important;
  width: 100%;
  border-radius: 12px !important;
  border: 3px solid ${({ borderColor }) => borderColor || 'transparent'};
  box-shadow: ${({ isSelected, borderColor }) =>
    isSelected && borderColor
      ? `0 0 20px ${borderColor}40, 0 0 8px ${borderColor}60, 0 4px 12px rgba(0, 0, 0, 0.15)`
      : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ isSelected, borderColor }) =>
      isSelected && borderColor
        ? `0 0 25px ${borderColor}50, 0 0 10px ${borderColor}70, 0 8px 20px rgba(0, 0, 0, 0.2)`
        : '0 8px 20px rgba(0, 0, 0, 0.15)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    &:hover {
      transform: none;
    }
  }

  > .ant-card-body {
    display: flex;
    flex-direction: column;
    padding: 20px;

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
      padding: 16px;
    }
  }
`
const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 120px;
  width: 100%;
  margin-bottom: 12px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px dashed #d9d9d9;
  transition: all 0.2s ease;

  &:hover {
    border-color: #b5d922;
    background: #f9fff0;
  }
`

const LogoInitials = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #e6f7ff;
  color: #1890ff;
  font-weight: 700;
  font-size: 28px;
  text-transform: uppercase;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
`
const Img = styled.img`
  max-height: 120px;
  max-width: 194px;
  border-radius: 6px;
  object-fit: contain;
`

const H3 = styled.h3`
  text-align: center;
  min-height: 66px;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 12px 0;
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    flex-direction: column;
    align-items: stretch;
    margin-top: 12px;
    padding-top: 8px;
  }
`

const ActionCard = styled(Card)`
  background: ${({ theme }) => theme.colours.mayoWhite};
  margin: ${({ theme }) => theme.spacing.sm} 0 !important;
  border-radius: 12px !important;
  width: calc(25% - 30px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
    width: calc(50% - 20px);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    width: 100%;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    &:hover {
      transform: none;
    }
  }

  > .ant-card-body {
    display: flex;
    flex-direction: column;
    padding: 20px;

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
      padding: 16px;
    }
  }

  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 16px;

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
      font-size: 14px;
      margin-bottom: 12px;
    }
  }

  input,
  .ant-select-selection--multiple {
    background: ${({ theme }) => theme.colours.bgGrey};
  }
`

const ActionButton = styled(Popconfirm)`
  width: 100px;
  align-self: flex-end;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    flex-direction: column;
    gap: 16px;
  }
`

const CardButton = styled(Popconfirm)`
  align-self: flex-end;
  margin-bottom: 10px;
  margin-left: 12px;
`

const InputComp = styled(Input)`
  margin-bottom: ${({ theme }) => theme.spacing.sm} !important;
`

const FilterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    flex-direction: column;
    padding: 12px;
    margin-bottom: 16px;
  }
`

const FilterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  padding-right: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    padding-right: 0;
    width: 100%;
  }
`

const FilterTopRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    input {
      width: 100% !important;
    }
  }
`

const FilterBottomRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    flex-direction: column;
    align-items: stretch;

    button,
    .ant-select {
      width: 100% !important;
    }
  }
`

const ActivityContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
`

const ActivityText = styled.span`
  font-size: 12px;
  color: #666;
  text-align: center;
  font-weight: 400;
  line-height: 1.5;
`

const CheckboxContainer = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 6px;
  padding: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transform: scale(1.05);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    top: 8px;
    left: 8px;
    padding: 4px;

    &:hover {
      transform: scale(1.02);
    }
  }
`

const CodeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  padding: 6px 12px;
  border-radius: 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  color: #1890ff;
  transition: all 0.2s ease;
  flex: 1;

  &:hover {
    background: #e6f7ff;
  }
`

const CodeText = styled.pre`
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  color: #1890ff;
  line-height: 1.4;
`

const CopyIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  color: #1890ff;
  flex-shrink: 0;

  .anticon {
    font-size: 16px;
    display: flex;
    align-items: center;
    color: #1890ff;
  }

  &:hover {
    background: rgba(24, 144, 255, 0.1);

    .anticon {
      color: #40a9ff;
    }
  }

  &:active {
    background: rgba(24, 144, 255, 0.2);
  }
`

const IncompleteSetup = styled.span`
  font-size: 12px;
  color: #999;
  font-style: italic;
`

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
  width: 100%;
`

const SkeletonCard = styled.div`
  width: 100%;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const SkeletonPlaceholder = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

const EmptyStateWrapper = styled.div`
  width: 100%;
  padding: 60px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

const EmptyDescription = styled.div`
  text-align: center;
`

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
`

const EmptyText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
  line-height: 1.6;
`

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`

const StyledTag = styled(Tag)`
  font-size: 13px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
  margin: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`

const SelectAllCheckboxWrapper = styled.div`
  .ant-checkbox-checked:after {
    border: none;
  }
`

const SelectAllText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 0;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    width: 100%;
    justify-content: center;
    padding: 0;
  }

  .ant-pagination {
    margin: 0;
  }

  .ant-pagination-item {
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    }

    &.ant-pagination-item-active {
      background: #b5d922;
      border-color: #b5d922;

      a {
        color: white;
        font-weight: 600;
      }
    }
  }

  .ant-pagination-prev,
  .ant-pagination-next {
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}px) {
    .ant-pagination {
      font-size: 12px;
    }

    .ant-pagination-item,
    .ant-pagination-prev,
    .ant-pagination-next {
      min-width: 28px;
      height: 28px;
      line-height: 28px;
    }
  }
`

export default Venues
